import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const C = { emails: "#0093D6", calls: "#00577F", first_interviews: "#0EA5A4", final_interviews: "#F59E0B",
  background_checks: "#8B5CF6", career_conversions: "#16A34A", completed: "#0EA5A4", cancelled: "#94A3B8", no_show: "#E11D48", upcoming: "#0093D6" };
const KPIS = [["emails", "Emails sent"], ["calls", "Calls documented"], ["first_interviews", "First interviews"],
  ["background_checks", "Background checks"], ["final_interviews", "Final interviews"], ["career_conversions", "Career conversions"]];
const short = (n) => { const s = n || ""; return s.length > 18 ? `${s.slice(0, 17)}…` : s; };

export function KpiStrip({ totals }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="ra-kpis">
      {KPIS.map(([k, l]) => (
        <div key={k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`ra-kpi-${k}`}>
          <span className="block w-6 h-1 rounded-full mb-3" style={{ background: C[k] }} />
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{totals[k] || 0}</p>
          <p className="text-xs text-slate-500 mt-0.5">{l}</p>
        </div>))}
    </div>
  );
}

function Chart({ title, testId, data, bars, stacked }) {
  const h = Math.max(220, data.length * 34 + 70);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <p className="font-semibold text-slate-800 mb-2">{title}</p>
      <ResponsiveContainer width="100%" height={h}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }} barCategoryGap="22%">
          <CartesianGrid horizontal={false} stroke="#EEF2F6" />
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="label" width={130} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ borderRadius: 8, fontSize: 12, borderColor: "#E2E8F0" }}
            labelFormatter={(l, p) => (p?.[0]?.payload ? `${p[0].payload.name} · ${p[0].payload.role_label}` : l)} />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
          {bars.map(([k, l], i) => <Bar key={k} dataKey={k} name={l} fill={C[k]} stackId={stacked ? "s" : undefined}
            radius={stacked ? (i === bars.length - 1 ? [0, 4, 4, 0] : 0) : [0, 4, 4, 0]} maxBarSize={14} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ActivityCharts({ items }) {
  const data = items.map((r) => ({ ...r, label: short(r.name), completed: Math.max(0, r.booked - r.cancelled - r.no_show - r.upcoming) }));
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="ra-charts">
      <Chart title="Outreach" testId="ra-chart-outreach" data={data} bars={[["emails", "Emails sent"], ["calls", "Calls documented"]]} />
      <Chart title="Interviews conducted" testId="ra-chart-interviews" data={data} bars={[["first_interviews", "First interviews"], ["final_interviews", "Final interviews"]]} />
      <Chart title="Candidate progress" testId="ra-chart-progress" data={data} bars={[["background_checks", "Background checks submitted"], ["career_conversions", "Career conversions"]]} />
      <Chart title="Meetings booked by outcome" testId="ra-chart-bookings" data={data} stacked
        bars={[["completed", "Held / past"], ["upcoming", "Upcoming"], ["cancelled", "Cancelled"], ["no_show", "No show"]]} />
    </div>
  );
}
